// components/PasswordResetForm.js
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth-service';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PasswordResetForm = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const navigate = useNavigate();

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authService.resetPassword(email);
      setResetSent(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="grey.100"
      px={2}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={3}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              Handoff
            </Typography>
            <Typography variant="h6" color="textSecondary">
              Reset Your Password
            </Typography>
          </Box>

          {!resetSent ? (
            <>
              <Typography variant="body2" color="textSecondary" mb={3} textAlign="center">
                Enter your email address and we'll send you a link to reset your password.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handlePasswordReset}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  required
                  autoFocus
                  autoComplete="email"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                  size="large"
                >
                  {loading ? <CircularProgress size={24} /> : 'Request Password Reset'}
                </Button>
              </form>

              <Box textAlign="center" mt={2}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleBackToLogin}
                  disabled={loading}
                  sx={{
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                  Back to Sign In
                </Link>
              </Box>
            </>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 3 }}>
                Password reset email sent!
              </Alert>

              <Typography variant="body2" color="textSecondary" mb={3} textAlign="center">
                Check your inbox at <strong>{email}</strong> for a link to reset your password.
                The link will expire in 1 hour.
              </Typography>

              <Typography variant="body2" color="textSecondary" mb={3} textAlign="center">
                Didn't receive the email? Check your spam folder or try again.
              </Typography>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setResetSent(false);
                  setEmail('');
                }}
                sx={{ mb: 2 }}
              >
                Send Another Email
              </Button>

              <Button
                fullWidth
                variant="contained"
                onClick={handleBackToLogin}
              >
                Back to Sign In
              </Button>
            </>
          )}

          <Box mt={3} textAlign="center">
            <Typography variant="caption" color="textSecondary">
              Need access? Contact your Handoff representative.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PasswordResetForm;
